import { useEffect, useRef } from 'react';
import { useChrome } from '../../lib/chrome';
import { usePrefersReducedMotion } from '../../hooks/useMediaQuery';
import styles from './Hero.module.css';

type Props = {
  title: string;
  subtitle: string;
  video?: string | undefined;
  poster?: string | undefined;
};

/**
 * Full-screen video pinned behind the page. The hero section itself is just a
 * viewport-tall spacer holding the overlay text; page content scrolls up over the video.
 */
export function Hero({ title, subtitle, video, poster }: Props) {
  const spacerRef = useRef<HTMLElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const reducedMotion = usePrefersReducedMotion();
  const { setOverHero } = useChrome();

  useEffect(() => {
    const spacer = spacerRef.current;
    if (!spacer) return;
    const headerH =
      parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-h')) || 64;

    // Header goes solid once the content sheet reaches it.
    const headerObserver = new IntersectionObserver(
      ([entry]) => setOverHero(Boolean(entry?.isIntersecting)),
      { rootMargin: `-${headerH}px 0px 0px 0px` },
    );
    // Pause the video once it is fully covered, to save battery and CPU.
    const videoObserver = new IntersectionObserver(([entry]) => {
      const v = videoRef.current;
      if (!v) return;
      if (entry?.isIntersecting) v.play().catch(() => {});
      else v.pause();
    });

    headerObserver.observe(spacer);
    videoObserver.observe(spacer);
    return () => {
      headerObserver.disconnect();
      videoObserver.disconnect();
      setOverHero(false);
    };
  }, [setOverHero]);

  useEffect(() => {
    // React does not reliably reflect `muted` to the DOM, which iOS needs for autoplay.
    if (videoRef.current) videoRef.current.muted = true;
  }, []);

  return (
    <>
      <div className={styles.backdrop} aria-hidden>
        {video && !reducedMotion ? (
          <video
            ref={videoRef}
            className={styles.media}
            src={video}
            poster={poster}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
          />
        ) : (
          poster && <img className={styles.media} src={poster} alt="" />
        )}
        <div className={styles.scrim} />
      </div>

      <section ref={spacerRef} className={styles.hero} aria-label="Introduction">
        <h1 className={styles.title}>{title}</h1>
        <p className={styles.subtitle}>{subtitle}</p>
        <a href="#work" className={styles.cue}>
          <span>Work</span>
          <span className={styles.cueLine} aria-hidden />
        </a>
      </section>
    </>
  );
}
